/**
 * AVA-475 — client-side PDF export of the verification report.
 *
 * A DUMB RENDERER. Every decision about what goes where — wrapping, page
 * breaks, the repeated header, "Page N of M", image sizing — belongs to
 * `reportLayout.js`, which produces the same draw commands the on-screen
 * preview consumes. This file only turns commands into jsPDF calls.
 *
 * That split is the whole point: the preview and the PDF used to paginate
 * independently, so "how many pages is this?" had no answer until you
 * downloaded it. Nothing here may measure text, decide a break, or position
 * content — the moment it does, the two documents can disagree again.
 *
 * TWO CONSTRAINTS SURVIVE HERE BECAUSE THEY ARE jsPDF's, NOT THE LAYOUT'S:
 *
 * 1. ASCII SIGNS ONLY. jsPDF's built-in Helvetica encodes as WinAnsi (cp1252).
 *    U+2212 MINUS SIGN is not in cp1252, so a -5 mm deviation would print with
 *    its sign dropped — a fabricated finding on a conformance document. The
 *    layout already emits `formatDeviation(v, true)`; do not "improve" the
 *    glyph on the way out.
 *
 * 2. THE LAYOUT RUNS TO COMPLETION BEFORE THE FIRST DRAW. `splitTextToSize`
 *    depends on the document's current font, so measuring and drawing must not
 *    interleave. `layoutReport` measures inside its own private jsPDF instance
 *    (see `createMeasurer`), and this file does not touch `doc` until the
 *    layout has returned.
 */
import jsPDF from 'jspdf';
import { log as debugLog } from '@/utils/debugLogger';
import { COLOR, layoutReport } from '@/features/Verification/utils/reportLayout';

/** Hairline width in mm for section rules. */
const RULE_WIDTH = 0.2;

/**
 * Evidence is always JPEG because we encode it; a reference drawing may be a
 * PNG. The layout states the encoding on the command, so nothing here sniffs.
 */
const DEFAULT_IMAGE_FORMAT = 'JPEG';

function applyColor(doc, color) {
  const [r, g, b] = color || COLOR.ink;
  doc.setTextColor(r, g, b);
}

function drawText(doc, command) {
  doc.setFontSize(command.size).setFont('helvetica', command.style);
  applyColor(doc, command.color);
  doc.text(command.text, command.x, command.y, { align: command.align });
}

function drawRule(doc, command) {
  const [r, g, b] = command.color || COLOR.rule;
  doc.setDrawColor(r, g, b);
  doc.setLineWidth(RULE_WIDTH);
  doc.line(command.x, command.y, command.x + command.width, command.y);
}

function drawImage(doc, command) {
  doc.addImage(
    command.dataUrl,
    command.format || DEFAULT_IMAGE_FORMAT,
    command.x,
    command.y,
    command.width,
    command.height,
  );
}

const DRAW = {
  text: drawText,
  rule: drawRule,
  image: drawImage,
};

/**
 * Render `VERIFICATION/GET_REPORT_MODEL` to a downloaded PDF.
 *
 * @param {object} model The same model the preview renders — never the DOM, so
 *   what downloads cannot drift from what was reviewed.
 */
export function generateReportPdf(model) {
  // Layout FIRST, completely — see note 2.
  const { pages, pageCount } = layoutReport(model);

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  pages.forEach((page, index) => {
    if (index > 0) doc.addPage();
    page.commands.forEach((command) => {
      const draw = DRAW[command.kind];
      if (draw) draw(doc, command);
    });
  });

  const support = model?.support || {};
  const filename = `${support.reportId || 'verification-report'}-Rev${support.revision || 'A'}.pdf`;
  debugLog('[reportPdf] saving', filename, {
    points: model?.points?.length ?? 0,
    pages: pageCount,
  });
  doc.save(filename);
}

export default generateReportPdf;
